import './Returns.css';
import '../../Dashboard.css';
import SERVER_URL from '../../../../env.js';
import { useEffect, useState, useMemo, useCallback } from 'react';
import Cookies from 'js-cookie';

/* ── helpers ─────────────────────────────────────────────────────── */
const fmt = (val) => {
    const n = parseFloat(val);
    return isNaN(n) ? '0.00' : n.toFixed(2);
};

const currentYear = new Date().getFullYear();

/* ── sub-component: expandable fee list per return ───────────────── */
function FeeBox({ fees }) {
    const [expanded, setExpanded] = useState(false);
    if (!fees || fees.length === 0)
        return <div className="cr-fees" style={{ cursor: 'default' }}>No fees</div>;
    if (fees.length === 1)
        return (
            <div className="cr-fees" style={{ cursor: 'default' }}>
                <span>{fees[0].fee_type}: <strong>${fmt(fees[0].amount)}</strong></span>
            </div>
        );
    return (
        <div className="cr-fees" onClick={() => setExpanded(v => !v)}>
            <strong >Fees:</strong>
            <span>{fees[0].fee_type}: <strong>${fmt(fees[0].amount)}</strong>
                {!expanded && <em className="cr-fees-more"> (+{fees.length - 1} more)</em>}
            </span>
            {expanded && fees.slice(1).map((f, i) => (
                <div key={i} className="cr-fee-item">{f.fee_type}: <strong>${fmt(f.amount)}</strong></div>
            ))}
        </div>
    );
}

/* ── main component ──────────────────────────────────────────────── */
function Returns({ currentClientID }) {
    const [returns, setReturns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Date range filter — default: last 5 years → current year
    const [fromYear, setFromYear] = useState(currentYear - 5);
    const [toYear, setToYear] = useState(currentYear);

    /* fetch whenever the selected client changes */
    const fetchReturns = useCallback(() => {
        if (!currentClientID) return;
        setLoading(true);
        setError('');
        fetch(`${SERVER_URL}/returns/client/${currentClientID}`, {
            headers: { 'Authorization': 'Bearer ' + Cookies.get('accessToken') },
        })
            .then(async (res) => {
                if (!res.ok) {
                    const d = await res.json().catch(() => ({}));
                    throw new Error(d.error || `HTTP ${res.status}`);
                }
                return res.json();
            })
            .then((data) => setReturns(data.returns || []))
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, [currentClientID]);

    useEffect(() => { fetchReturns(); }, [fetchReturns]);

    /* year-range filter */
    const filtered = useMemo(() =>
        returns.filter(r => {
            const y = parseInt(r.year, 10);
            return y >= fromYear && y <= toYear;
        }),
        [returns, fromYear, toYear]
    );

    /* ── aggregates across filtered returns ── */
    const totals = useMemo(() => {
        const feeMap = {};
        let cash = 0, etransfer = 0, outstanding = 0, feesTotal = 0;

        filtered.forEach(r => {
            cash += parseFloat(r.cash) || 0;
            etransfer += parseFloat(r.e_transfer_or_cheque) || 0;
            outstanding += parseFloat(r.outstanding_balance) || 0;
            (r.fees || []).forEach(f => {
                const amt = parseFloat(f.amount) || 0;
                feesTotal += amt;
                feeMap[f.fee_type] = (feeMap[f.fee_type] || 0) + amt;
            });
        });

        return { cash, etransfer, outstanding, feeMap, feesTotal };
    }, [filtered]);

    /* ── guard: no client selected ── */
    if (!currentClientID) {
        return (
            <div className="cr-empty">
                <p>No Client Selected</p>
                <small>Select a client from the Search tab to view their returns.</small>
            </div>
        );
    }

    const clientName = returns[0]?.client_name || '';

    return (
        <>
            <div className="cr-wrapper">

                {/* ── header: client name + year range ── */}
                <div className="cr-header">
                    <div className="cr-header-top">
                        {clientName && <h2 className="cr-client-name">{clientName}</h2>}
                        <div className="cr-year-range">
                            <label className="cr-range-label">From</label>
                            <input
                                type="number"
                                className="cr-year-input"
                                min={1900}
                                value={fromYear}
                                onChange={e => setFromYear(parseInt(e.target.value, 10) || currentYear - 4)}
                            />
                            <label className="cr-range-label">To</label>
                            <input
                                type="number"
                                className="cr-year-input"
                                min={1900}
                                value={toYear}
                                onChange={e => setToYear(parseInt(e.target.value, 10) || currentYear)}
                            />
                        </div>
                    </div>

                    {/* ── summary panel ── */}
                    {filtered.length > 0 && (
                        <div className="cr-summary">
                            {/* payment totals */}
                            <div className="cr-summary-block">
                                <div className="cr-summary-title">Totals</div>
                                <div className="cr-summary-row">
                                    <span className="cr-summary-key">Cash</span>
                                    <span className="cr-summary-val">${fmt(totals.cash)}</span>
                                </div>
                                <div className="cr-summary-row">
                                    <span className="cr-summary-key">E-Transfer / Cheque</span>
                                    <span className="cr-summary-val">${fmt(totals.etransfer)}</span>
                                </div>
                                <div className="cr-summary-row cr-summary-row--outstanding">
                                    <span className="cr-summary-key">Outstanding Balance</span>
                                    <span className="cr-summary-val">${fmt(totals.outstanding)}</span>
                                </div>
                            </div>

                            {/* fee breakdown */}
                            {Object.keys(totals.feeMap).length > 0 && (
                                <div className="cr-summary-block">
                                    <div className="cr-summary-title">
                                        Fees
                                        <span className="cr-fees-total">${fmt(totals.feesTotal)}</span>
                                    </div>
                                    {Object.entries(totals.feeMap).map(([type, amt]) => (
                                        <div key={type} className="cr-summary-row">
                                            <span className="cr-summary-key">{type}</span>
                                            <span className="cr-summary-val">${fmt(amt)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <div className="cr-wrapper-second">
                {/* ── body ── */}
                {error && <div className="cr-error">{error}</div>}

                {loading ? (
                    <div className="cr-state">Loading returns…</div>
                ) : filtered.length === 0 ? (
                    <div className="cr-state">No returns found for {fromYear}–{toYear}.</div>
                ) : (
                    <div className="cr-list">
                        {filtered.map((ret) => (
                            <div key={ret.id} className="cr-row">
                                <div className="cr-row-main">
                                    {/* meta tags */}
                                    <div className="cr-meta">
                                        <span className="cr-tag cr-tag--year">{ret.year}</span>
                                        <span className={`cr-tag cr-tag--status cr-tag--status-${(ret.status || 'Open').toLowerCase().replace(/\s+/g, '-')}`}>
                                            {ret.status || 'Open'}
                                        </span>
                                        {ret.invoice_number && (
                                            <span className="cr-tag cr-tag--invoice">Invoice #{ret.invoice_number}</span>
                                        )}
                                        {ret.last_contact_date && (
                                            <span className="cr-tag cr-tag--date">
                                                Last Contact: {new Date(ret.last_contact_date).toLocaleDateString()}
                                            </span>
                                        )}
                                    </div>

                                    {/* financials */}
                                    <div className="cr-financials">
                                        {parseFloat(ret.cash) > 0 && (
                                            <div className="cr-fin-item">
                                                <span className="cr-fin-label">Cash</span>
                                                <span className="cr-fin-val">${fmt(ret.cash)}</span>
                                            </div>
                                        )}
                                        {parseFloat(ret.e_transfer_or_cheque) > 0 && (
                                            <div className="cr-fin-item">
                                                <span className="cr-fin-label">E-Transfer / Cheque</span>
                                                <span className="cr-fin-val">${fmt(ret.e_transfer_or_cheque)}</span>
                                            </div>
                                        )}
                                        {parseFloat(ret.outstanding_balance) > 0 && (
                                            <div className="cr-fin-item cr-fin-item--outstanding">
                                                <span className="cr-fin-label">Outstanding</span>
                                                <span className="cr-fin-val">${fmt(ret.outstanding_balance)}</span>
                                            </div>
                                        )}
                                    </div>
                                    {/* notes */}
                                    {ret.notes && (
                                        <div className="cr-notes">
                                            <strong>Notes:</strong> {ret.notes}
                                        </div>
                                    )}

                                    {/* fees */}
                                    <FeeBox fees={ret.fees} />

                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </>
    );
}

export default Returns;
